import cart from 'lib/cart'
import store from 'store'
import { formatPrice } from 'utils/general'

const DEFAULT_VARIANT_TITLE = 'Default Title'

function createElement (tagName, className, text) {
  const element = document.createElement(tagName)

  if (className) {
    element.className = className
  }

  if (text) {
    element.textContent = text
  }

  return element
}

function getSameOriginUrl (value) {
  try {
    const url = new URL(value, window.location.origin)

    return url.origin === window.location.origin ? url : null
  } catch (error) {
    return null
  }
}

function getProductJsonUrl (productUrl) {
  const url = getSameOriginUrl(productUrl)

  if (!url) {
    return null
  }

  url.search = ''
  url.hash = ''
  url.pathname = `${url.pathname.replace(/\/$/, '')}.js`

  return url.toString()
}

function getVariant (product, item) {
  const variants = Array.isArray(product.variants) ? product.variants : []

  return variants.find(variant => String(variant.id) === String(item.epi)) || null
}

function getOptionPairs (product, variant) {
  if (!variant) {
    return []
  }

  const options = Array.isArray(product.options) ? product.options : []
  const values = Array.isArray(variant.options)
    ? variant.options
    : [variant.option1, variant.option2, variant.option3].filter(Boolean)

  return values.map((value, index) => {
    const option = options[index]

    return {
      name: typeof option === 'object' ? option.name : option,
      value
    }
  }).filter(option => option.value)
}

function getProductUrl (item, product) {
  const productUrl = getSameOriginUrl(product.url || item.du)

  return productUrl ? productUrl.toString() : '#'
}

class RedesignAccountWishlist {
  constructor (element) {
    this.element = element
    this.status = element.querySelector('[data-wishlist-status]')
    this.empty = element.querySelector('[data-wishlist-empty]')
    this.grid = element.querySelector('[data-wishlist-grid]')
    this.actions = element.querySelector('[data-wishlist-actions]')
    this.shareDialog = element.querySelector('[data-wishlist-share-dialog]')
    this.shareForm = element.querySelector('[data-wishlist-share-form]')
    this.shareFeedback = element.querySelector('[data-wishlist-share-feedback]')
    this.items = []
    this.cards = []
    this.swat = null
    this.isReady = false

    this.onAddAll = this.onAddAll.bind(this)
    this.onClear = this.onClear.bind(this)
    this.onShare = this.onShare.bind(this)
    this.onShareSubmit = this.onShareSubmit.bind(this)
    this.closeShareDialog = this.closeShareDialog.bind(this)

    this.bindEvents()
    this.registerSwymCallback()
  }

  bindEvents () {
    this.element.querySelector('[data-wishlist-add-all]').addEventListener('click', this.onAddAll)
    this.element.querySelector('[data-wishlist-clear]').addEventListener('click', this.onClear)
    this.element.querySelector('[data-wishlist-share]').addEventListener('click', this.onShare)
    this.element.querySelector('[data-wishlist-share-close]').addEventListener('click', this.closeShareDialog)
    this.shareForm.addEventListener('submit', this.onShareSubmit)
  }

  registerSwymCallback () {
    window.SwymCallbacks = window.SwymCallbacks || []
    window.SwymCallbacks.push(swat => {
      if (this.isReady) {
        return
      }

      this.isReady = true
      this.swat = swat
      this.load()
    })
  }

  load () {
    this.setStatus(this.status.textContent)

    this.fetchItems()
      .then(items => {
        this.items = items

        if (!items.length) {
          this.renderEmpty()
          return null
        }

        return Promise.all(items.map(item => this.fetchProduct(item)))
          .then(cards => {
            this.cards = cards
            this.renderCards()
          })
      })
      .catch(() => this.renderError())
  }

  fetchItems () {
    return new Promise((resolve, reject) => {
      this.swat.fetch(resolve, reject)
    })
  }

  fetchProduct (item) {
    const productUrl = getProductJsonUrl(item.du)

    if (!productUrl) {
      return Promise.resolve({ item, product: this.createFallbackProduct(item) })
    }

    return fetch(productUrl, { credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to load product')
        }

        return response.json()
      })
      .then(product => ({ item, product }))
      .catch(() => ({ item, product: this.createFallbackProduct(item) }))
  }

  createFallbackProduct (item) {
    return {
      title: item.dt,
      url: item.du,
      featured_image: item.iu,
      variants: []
    }
  }

  renderCards () {
    this.grid.innerHTML = ''

    for (const card of this.cards) {
      this.grid.appendChild(this.createCard(card))
    }

    this.status.hidden = true
    this.empty.hidden = true
    this.grid.hidden = false
    this.actions.hidden = false
  }

  renderEmpty () {
    this.status.hidden = true
    this.empty.hidden = false
    this.grid.hidden = true
    this.actions.hidden = true
  }

  renderError () {
    this.setStatus(this.element.dataset.saveError)
    this.empty.hidden = true
    this.grid.hidden = true
    this.actions.hidden = true
  }

  createCard (card) {
    const item = card.item
    const product = card.product
    const variant = getVariant(product, item)
    const optionPairs = getOptionPairs(product, variant)
    const listItem = createElement('li', 'redesign-account-wishlist__item')
    const article = createElement('article', 'redesign-product-card redesign-account-wishlist-card')
    const link = createElement('a', 'redesign-product-card__link')
    const media = createElement('div', 'redesign-product-card__media')
    const image = createElement('img', 'redesign-product-card__image')
    const details = createElement('div', 'redesign-product-card__details')
    const title = createElement('p', 'redesign-product-card__title', product.title || item.dt)
    const material = optionPairs.find(option => /material|metal|colour|color|finish/i.test(option.name || ''))
    const size = optionPairs.find(option => /size/i.test(option.name || ''))
    const fallbackVariant = variant && variant.title !== DEFAULT_VARIANT_TITLE ? variant.title : ''
    const selectedMaterial = material ? material.value : (!size ? fallbackVariant : '')
    const selectedSize = size ? size.value : ''
    const price = variant ? variant.price : item.pr
    const imageSource = product.featured_image || item.iu
    const removeButton = createElement('button', 'redesign-account-wishlist-card__remove')

    link.href = getProductUrl(item, product)
    link.setAttribute('aria-label', product.title || item.dt || '')

    image.src = imageSource || ''
    image.alt = product.title || item.dt || ''
    image.loading = 'lazy'
    image.decoding = 'async'

    media.appendChild(image)
    link.appendChild(media)
    details.appendChild(title)

    if (selectedMaterial) {
      details.appendChild(createElement('p', 'redesign-account-wishlist-card__variant', selectedMaterial))
    }

    details.appendChild(createElement('p', 'redesign-product-card__price', this.formatPrice(price)))

    if (selectedSize) {
      details.appendChild(createElement('p', 'redesign-account-wishlist-card__size', `Size: ${selectedSize}`))
    }

    link.appendChild(details)
    removeButton.type = 'button'
    removeButton.setAttribute('aria-label', `${this.element.dataset.removeLabel}: ${product.title || item.dt}`)
    removeButton.addEventListener('click', () => this.removeItem(card))

    article.appendChild(link)
    article.appendChild(removeButton)
    listItem.appendChild(article)

    return listItem
  }

  formatPrice (price) {
    const amount = Number(price)

    if (Number.isNaN(amount)) {
      return ''
    }

    return formatPrice(amount)
  }

  removeItem (card) {
    this.setActionsDisabled(true)

    this.removeSwymItem(card.item)
      .then(() => {
        this.items = this.items.filter(item => item !== card.item)
        this.cards = this.cards.filter(existingCard => existingCard !== card)

        if (this.cards.length) {
          this.renderCards()
        } else {
          this.renderEmpty()
        }
      })
      .catch(() => this.setStatus(this.element.dataset.saveError))
      .then(() => this.setActionsDisabled(false))
  }

  removeSwymItem (item) {
    const product = {
      epi: item.epi,
      empi: item.empi,
      du: item.du
    }

    if (typeof this.swat.removeFromWishList === 'function') {
      return new Promise((resolve, reject) => {
        this.swat.removeFromWishList(product, resolve, reject)
      }).catch(() => this.deleteFromList(item, product))
    }

    return this.deleteFromList(item, product)
  }

  deleteFromList (item, product) {
    if (typeof this.swat.deleteFromList !== 'function') {
      return Promise.reject(new Error('Swym remove API unavailable'))
    }

    return this.getListId(item)
      .then(listId => new Promise((resolve, reject) => {
        this.swat.deleteFromList(listId, product, resolve, reject)
      }))
  }

  getListId (item) {
    if (item.lid) {
      return Promise.resolve(item.lid)
    }

    if (typeof this.swat.fetchLists !== 'function') {
      return Promise.reject(new Error('Swym list API unavailable'))
    }

    return new Promise((resolve, reject) => {
      this.swat.fetchLists({
        callbackFn: lists => {
          const list = lists.find(currentList => {
            return currentList.listcontents && currentList.listcontents.some(listItem => {
              return String(listItem.epi) === String(item.epi) && String(listItem.empi) === String(item.empi)
            })
          })

          if (list) {
            resolve(list.lid)
            return
          }

          reject(new Error('Wishlist list not found'))
        },
        errorFn: reject
      })
    })
  }

  onAddAll () {
    const availableCards = this.cards.filter(card => {
      const variant = getVariant(card.product, card.item)
      return variant && variant.available
    })

    if (!availableCards.length) {
      this.setStatus(this.element.dataset.noAvailableItems)
      return
    }

    this.setActionsDisabled(true)

    availableCards.reduce((chain, card) => {
      const variant = getVariant(card.product, card.item)

      return chain.then(() => cart.add({ id: variant.id, quantity: 1 }))
        .then(result => {
          if (result.errors) {
            throw new Error(result.errors[0])
          }
        })
    }, Promise.resolve())
      .then(() => store.dispatch('cart/getCart'))
      .then(() => store.dispatch('cart/openCart'))
      .then(() => this.setStatus(this.element.dataset.addAllSuccess))
      .catch(() => this.setStatus(this.element.dataset.saveError))
      .then(() => this.setActionsDisabled(false))
  }

  onClear () {
    this.setActionsDisabled(true)

    this.items.reduce((chain, item) => chain.then(() => this.removeSwymItem(item)), Promise.resolve())
      .then(() => {
        this.items = []
        this.cards = []
        this.renderEmpty()
      })
      .catch(() => this.setStatus(this.element.dataset.saveError))
      .then(() => this.setActionsDisabled(false))
  }

  onShare () {
    this.shareFeedback.textContent = ''

    if (typeof this.shareDialog.showModal === 'function') {
      this.shareDialog.showModal()
      return
    }

    this.shareDialog.setAttribute('open', '')
  }

  onShareSubmit (event) {
    event.preventDefault()

    if (!this.shareForm.reportValidity()) {
      return
    }

    const sender = this.element.querySelector('[data-wishlist-share-sender]').value.trim()
    const recipient = this.element.querySelector('[data-wishlist-share-recipient]').value.trim()
    const message = this.element.querySelector('[data-wishlist-share-message]').value.trim()
    const firstItem = this.items[0]

    if (typeof this.swat.sendListViaEmail !== 'function' || !firstItem) {
      this.shareFeedback.textContent = this.element.dataset.saveError
      return
    }

    this.setShareDisabled(true)

    this.swat.sendListViaEmail({
      toEmailId: recipient,
      fromName: sender,
      note: message,
      lid: firstItem.lid || null,
      cprops: {}
    }, () => {
      this.shareFeedback.textContent = this.element.dataset.shareSuccess
      this.setShareDisabled(false)
    }, () => {
      this.shareFeedback.textContent = this.element.dataset.saveError
      this.setShareDisabled(false)
    })
  }

  closeShareDialog () {
    if (typeof this.shareDialog.close === 'function') {
      this.shareDialog.close()
      return
    }

    this.shareDialog.removeAttribute('open')
  }

  setActionsDisabled (disabled) {
    const buttons = this.element.querySelectorAll('[data-wishlist-add-all], [data-wishlist-clear], [data-wishlist-share], .redesign-account-wishlist-card__remove')

    for (const button of buttons) {
      button.disabled = disabled
    }
  }

  setShareDisabled (disabled) {
    this.element.querySelector('.redesign-account-wishlist__share-submit').disabled = disabled
  }

  setStatus (message) {
    this.status.textContent = message
    this.status.hidden = false
  }
}

export default element => new RedesignAccountWishlist(element)
